/**
 * Error codes for purchase operations
 */
export enum PurchaseErrorCode {
  SIGNATURE_USED = 'SIGNATURE_USED',
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  WRONG_NETWORK = 'WRONG_NETWORK',
  PERMISSIONS_REJECTED = 'PERMISSIONS_REJECTED',
  USER_CANCELED = 'USER_CANCELED',
  INVALID_SELLER_ADDRESS = 'INVALID_SELLER_ADDRESS',
  AMOUNT_OVERFLOW = 'AMOUNT_OVERFLOW',
  NO_AVAILABLE_OFFERS = 'NO_AVAILABLE_OFFERS',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Custom error class for purchase operations
 */
export class PurchaseError extends Error {
  constructor(
    message: string,
    public readonly code: PurchaseErrorCode,
    public readonly recoverable: boolean,
    public readonly originalError?: unknown
  ) {
    super(message);
    this.name = 'PurchaseError';

    // Maintain proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, PurchaseError);
    }
  }

  static signatureUsed(message = 'This offer has already been used.'): PurchaseError {
    return new PurchaseError(message, PurchaseErrorCode.SIGNATURE_USED, true);
  }

  static insufficientFunds(message = 'Insufficient funds for purchase.'): PurchaseError {
    return new PurchaseError(message, PurchaseErrorCode.INSUFFICIENT_FUNDS, false);
  }

  static wrongNetwork(message = 'Wrong network selected.'): PurchaseError {
    return new PurchaseError(message, PurchaseErrorCode.WRONG_NETWORK, false);
  }

  static permissionsRejected(message = 'Currency permissions rejected.'): PurchaseError {
    return new PurchaseError(message, PurchaseErrorCode.PERMISSIONS_REJECTED, false);
  }

  static userCanceled(message = 'User denied transaction signature.'): PurchaseError {
    return new PurchaseError(message, PurchaseErrorCode.USER_CANCELED, false);
  }

  static invalidSellerAddress(address: string): PurchaseError {
    return new PurchaseError(`Invalid seller address: ${address}`, PurchaseErrorCode.INVALID_SELLER_ADDRESS, false);
  }

  static amountOverflow(message = 'Amount exceeds safe limits.'): PurchaseError {
    return new PurchaseError(message, PurchaseErrorCode.AMOUNT_OVERFLOW, false);
  }

  static noAvailableOffers(message = 'No available offers found.'): PurchaseError {
    return new PurchaseError(message, PurchaseErrorCode.NO_AVAILABLE_OFFERS, false);
  }

  static networkError(originalError: unknown): PurchaseError {
    return new PurchaseError(
      'Network error occurred during purchase.',
      PurchaseErrorCode.NETWORK_ERROR,
      true,
      originalError
    );
  }

  static unknown(originalError: unknown): PurchaseError {
    return new PurchaseError(
      'An unknown error occurred during purchase.',
      PurchaseErrorCode.UNKNOWN_ERROR,
      false,
      originalError
    );
  }
}
